import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useEffect } from 'react';

import { FCC } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { FeatureFlagKeysLookup } from '@eluve/posthog';
import { useSession, useUserIdFromSession } from '@eluve/session-helpers';

import { appConfig } from './config';

// Default to false when PostHog fails (can be changed to true in the future)
const INTERCOM_DEFAULT_WHEN_POSTHOG_FAILS = false;

export const IntercomProvider: FCC = ({ children }) => {
  const logger = useNamedLogger('IntercomProvider');
  const userId = useUserIdFromSession();
  const session = useSession();

  const isIntercomEnabled =
    useFeatureFlagEnabled(FeatureFlagKeysLookup.INTERCOM_INTEGRATION) ??
    INTERCOM_DEFAULT_WHEN_POSTHOG_FAILS;

  useEffect(() => {
    if (!isIntercomEnabled || !userId || !session || !session.email) {
      return;
    }

    try {
      const userName =
        [session.firstName, session.lastName].filter(Boolean).join(' ') ||
        session.email;

      Intercom({
        app_id: appConfig.VITE_INTERCOM_APP_ID,
        user_id: userId,
        name: userName,
        email: session.email,
        created_at: session.createdAt,
      });

      logger.info('Intercom initialized successfully');
    } catch (error) {
      logger.error('Failed to load Intercom', { error });
    }
  }, [
    isIntercomEnabled,
    userId,
    session?.email,
    session?.firstName,
    session?.lastName,
    session?.createdAt,
    logger,
    session,
  ]);

  // Shutdown Intercom when feature flag is disabled
  useEffect(() => {
    if (!isIntercomEnabled && userId && session?.email) {
      try {
        shutdown();
        logger.info('Intercom shut down due to feature flag disabled');
      } catch (error) {
        logger.error('Failed to shut down Intercom', { error });
      }
    }
  }, [isIntercomEnabled, userId, session?.email, logger]);

  return children;
};
